import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useLang } from "@/context/LangContext";
import { formatCr, pick } from "@/lib/insights";

const URL_RE = /(https?:\/\/[^\s\]]+)/g;
const Linkified = ({ text }) =>
  text.split(URL_RE).map((part, i) =>
    i % 2 ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline decoration-1 underline-offset-2 hover:text-[#1a1640]">{part}</a> : part
  );

const AUDIENCE_COLORS = ["#d62828", "#f2b705", "#2f6fdb", "#2a9d8f", "#7c3aed"];

function RangeBar({ item, max, idx }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const same = item.low === item.high;
  return (
    <div className="card-in" style={{ animationDelay: `${idx * 60}ms` }} data-testid={`big-promise-${item.ref}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className={`text-[14px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>
          {pick(item, "promise", lang)}
          <span className={`block sm:inline sm:ml-2 text-[12px] font-medium text-[#6b6894] ${ta ? "font-tamil" : ""}`}>{pick(item, "audience", lang)} · {pick(item, "rate", lang)}</span>
        </div>
        <div className="text-[14px] font-bold tabular-nums text-[#1a1640] whitespace-nowrap" data-testid={`big-promise-value-${item.ref}`}>
          {same ? formatCr(item.low) : `${formatCr(item.low)}–${item.high.toLocaleString("en-IN")}`} <span className="text-[11px] font-semibold text-[#8c89ad]">{t("cr")}</span>
        </div>
      </div>
      <div className="mt-1.5 h-4 w-full rounded-full bg-[#f3eedc] overflow-hidden flex">
        <div className="h-full bg-[#1a1640] rounded-l-full" style={{ width: `${(item.low / max) * 100}%` }} />
        {!same && <div className="h-full bg-[#8f88c9]" style={{ width: `${((item.high - item.low) / max) * 100}%` }} />}
      </div>
    </div>
  );
}

function AudienceDonut({ items }) {
  const { t, lang } = useLang();
  const by = {};
  for (const i of items) {
    const k = pick(i, "audience", lang);
    by[k] = (by[k] || 0) + i.low;
  }
  const data = Object.entries(by).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const total = data.reduce((a, d) => a + d.value, 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4" data-testid="big-promise-donut">
      <div className="w-44 h-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2} stroke="none">
              {data.map((d, i) => <Cell key={d.name} fill={AUDIENCE_COLORS[i % AUDIENCE_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => `${formatCr(v)} ${t("cr")}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className={`space-y-1.5 text-[13px] ${lang === "ta" ? "font-tamil" : ""}`}>
        {data.map((d, i) => (
          <li key={d.name} className="flex items-center gap-2 text-[#1a1640]">
            <span className="w-3 h-3 rounded-sm" style={{ background: AUDIENCE_COLORS[i % AUDIENCE_COLORS.length] }} />
            <span className="font-semibold">{d.name}</span>
            <span className="text-[#6b6894] tabular-nums">{formatCr(d.value)} {t("cr")} · {Math.round((d.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BigPromisesChart({ data }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const max = Math.max(...data.items.map((i) => i.high));
  return (
    <section className="bg-white rounded-[22px] p-5 sm:p-7" data-testid="big-promises-chart">
      <h3 className={`text-xl font-semibold text-[#1a1640] ${ta ? "font-tamil" : "font-display"}`}>{t("big_cost_title")}</h3>
      <p className={`mt-1 text-[13px] text-[#6b6894] ${ta ? "font-tamil" : ""}`}>{t("big_cost_sub")}</p>
      <div className="mt-3 flex gap-4 text-[11.5px] text-[#6b6894]">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#1a1640]" />{t("low_label")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#8f88c9]" />{t("high_label")}</span>
      </div>
      <div className="mt-4 space-y-4">
        {data.items.map((i, idx) => <RangeBar key={i.ref} item={i} max={max} idx={idx} />)}
      </div>
      {data.total && (
        <div className="mt-6 rounded-2xl bg-[#1a1640] text-white p-5 flex flex-wrap items-baseline justify-between gap-2" data-testid="big-promises-total">
          <span className={`text-[14px] font-semibold ${ta ? "font-tamil" : ""}`}>{t("total_label")}</span>
          <span className="font-display text-2xl font-semibold tabular-nums">
            {formatCr(data.total.low)}–{data.total.high.toLocaleString("en-IN")} <span className="text-sm font-sans-custom opacity-80">{t("cr_year")}</span>
          </span>
        </div>
      )}
      <div className="mt-6"><AudienceDonut items={data.items} /></div>
      {data.context.length > 0 && (
        <div className="mt-6 rounded-2xl bg-[#f6f3ea] p-4" data-testid="big-promises-context">
          <h4 className={`text-[13px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("context_title")}</h4>
          <ul className={`mt-2 space-y-1.5 text-[12.5px] text-[#4b4870] list-disc pl-5 break-words ${ta ? "font-tamil" : ""}`}>
            {data.context.map((c, i) => <li key={i}><Linkified text={pick(c, "text", lang)} /></li>)}
          </ul>
        </div>
      )}
    </section>
  );
}
